// pages/JewishThoughtSubTopicDetail.js
import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { fetchWithAuth } from '../auth';
import Breadcrumbs from '../components/Breadcrumbs';
import { SkeletonCardGrid } from '../components/ui';
import { useResourceDetailModal } from '../components/ResourceDetailModalContext';

export default function JewishThoughtSubTopicDetail() {
  const { id } = useParams();
  const [subtopic, setSubtopic] = useState(null);
  const [files, setFiles] = useState([]);
  const [sources, setSources] = useState([]);
  const [generalMaterials, setGeneralMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        // Get subtopic details
        const res = await fetchWithAuth(`/api/jewish-thought-subtopics/${id}/`);
        if (!res.ok) throw new Error('Failed');
        const data = await res.json();
        if (!ignore) setSubtopic(data);

        // Get files (for מבוא למחשבת ישראל)
        const filesRes = await fetchWithAuth(`/api/jewish-thought-subtopics/${id}/files/`);
        if (filesRes.ok) {
          const filesData = await filesRes.json();
          if (!ignore) setFiles(filesData);
        }

        // Get sources (for פרק א, ב, ד)
        const sourcesRes = await fetchWithAuth(`/api/jewish-thought-subtopics/${id}/sources/`);
        if (sourcesRes.ok) {
          const sourcesData = await sourcesRes.json();
          if (!ignore) setSources(sourcesData.filter(s => s.is_enabled));
        }

        // Get general materials (for פרק א, ב, ד)
        const generalRes = await fetchWithAuth(`/api/jewish-thought-subtopic-general-material-files/?subtopic=${id}`);
        if (generalRes.ok) {
          const generalData = await generalRes.json();
          if (!ignore) setGeneralMaterials(generalData);
        }
      } catch {
        if (!ignore) setError('שגיאה בטעינת התוכן');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [id]);

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="h-6 w-64 bg-secondary-200 dark:bg-secondary-700 rounded animate-pulse mb-6" />
        <div className="h-8 w-32 bg-secondary-200 dark:bg-secondary-700 rounded animate-pulse mx-auto mb-8" />
        <SkeletonCardGrid count={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="text-center py-8">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!subtopic) return null;

  const groupedGeneralMaterials = generalMaterials.reduce((acc, m) => {
    const category = m.category || 'OTHER';
    if (!acc[category]) acc[category] = [];
    acc[category].push(m);
    return acc;
  }, {});

  const categoryLabels = {
    'SUMMARIES_TESTS': 'סיכומים ומבחנים',
    'ANNUAL_PLAN': 'תכנית שנתית לפרק'
  };

  return (
    <div className="page-wrapper">
      <Breadcrumbs
        items={[
          { to: '/disciplines', label: 'תחומי דעת' },
          { to: '/jewish-thought/subtopics', label: 'מחשבת ישראל' },
          { label: subtopic.name },
        ]}
      />

      <div className="toolbar-center">
        <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 dark:text-white">
          {subtopic.name}
        </h2>
      </div>

      <p className="text-center mt-2 mb-6">
        <Link
          to="/jewish-thought/subtopics"
          className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
        >
          חזרה לנושאים
        </Link>
      </p>

      {/* Files section (for מבוא למחשבת ישראל) */}
      {files.length > 0 && (
        <section className="mt-8">
          <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">קבצים</h3>
          <div className="grid-autofit">
            {files.map(file => (
              <ResourceCard key={file.id} item={file} apiSlug="jewish-thought-subtopic-files" />
            ))}
          </div>
        </section>
      )}

      {/* Sources section (for פרק א, ב, ד) */}
      {sources.length > 0 && (
        <section className="mt-8">
          <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">מקורות</h3>
          <div className="grid-autofit">
            {sources.map(source => (
              <SourceCard key={source.id} source={source} subtopicId={id} />
            ))}
          </div>
        </section>
      )}

      {/* General materials section (for פרק א, ב, ד) */}
      {Object.keys(groupedGeneralMaterials).length > 0 && (
        <section className="mt-8">
          <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">חומרים כלליים</h3>
          {Object.entries(groupedGeneralMaterials).map(([category, items]) => (
            <div key={category} className="mt-6">
              <h4 className="text-lg font-medium text-secondary-800 dark:text-secondary-200 mb-3">
                {categoryLabels[category] || category}
              </h4>
              <div className="grid-autofit">
                {items.map(material => (
                  <ResourceCard key={material.id} item={material} apiSlug="jewish-thought-subtopic-general-material-files" />
                ))}
              </div>
            </div>
          ))}
        </section>
      )}

      {files.length === 0 && sources.length === 0 && generalMaterials.length === 0 && (
        <p className="text-center text-secondary-500 dark:text-secondary-400 py-8">
          אין תוכן להצגה.
        </p>
      )}
    </div>
  );
}

// Source card component
function SourceCard({ source, subtopicId }) {
  return (
    <article className="group bg-white dark:bg-secondary-800 rounded-2xl shadow-card overflow-hidden transition-all duration-300 hover:shadow-card-hover hover:-translate-y-1">
      <Link
        to={`/jewish-thought/subtopics/${subtopicId}/sources/${source.id}`}
        className="block relative overflow-hidden bg-secondary-100 dark:bg-secondary-700"
        style={{ aspectRatio: '3/2' }}
      >
        {source.image ? (
          <img
            src={source.image}
            alt={source.name}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <svg className="w-12 h-12 text-secondary-300 dark:text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
        )}
      </Link>
      <div className="p-4 md:p-5">
        <h4 className="text-lg font-semibold text-secondary-900 dark:text-white text-center">
          <Link
            to={`/jewish-thought/subtopics/${subtopicId}/sources/${source.id}`}
            className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
          >
            {source.name}
          </Link>
        </h4>
      </div>
    </article>
  );
}

function ResourceCard({ item, apiSlug }) {
  const { openResourceDetail } = useResourceDetailModal();
  const hasFile = !!item.file;
  const isExternal = !!item.url && !hasFile;
  const downloadUrl = hasFile && apiSlug
    ? `/api/${apiSlug}/${item.id}/download/`
    : item.file || item.url;

  const handleOpen = () => {
    if (!item.use_detail_modal && downloadUrl) {
      window.open(downloadUrl, isExternal ? '_blank' : '_self', isExternal ? 'noopener,noreferrer' : undefined);
      return;
    }
    const modalItem = {
      id: item.id,
      model_type: 'jewish_thought_file',
      title: item.title || 'קובץ ללא כותרת',
      summary: item.explanation || '',
      explanation: item.explanation || '',
      author: item.author || '',
      subjectLabel: item.discipline_name || 'מחשבת ישראל',
      typeLabel: item.resource_type_label || 'קובץ',
      grades: item.grades || [],
      tags: [],
      downloadUrl,
      date: item.created_at || '',
    };
    openResourceDetail(modalItem);
  };
  return (
    <article className="group bg-white dark:bg-secondary-800 rounded-2xl shadow-card overflow-hidden transition-all duration-300 hover:shadow-card-hover hover:-translate-y-1">
      <button
        type="button"
        onClick={handleOpen}
        className="block w-full relative overflow-hidden bg-secondary-100 dark:bg-secondary-700"
        style={{ aspectRatio: '3/2' }}
      >
        {item.image ? (
          <img src={item.image} alt={item.title || 'קובץ'} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <svg className="w-12 h-12 text-secondary-300 dark:text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
        )}
      </button>
      <div className="p-4 md:p-5">
        <h4 className="text-lg font-semibold text-secondary-900 dark:text-white text-center">
          <button
            type="button"
            onClick={handleOpen}
            className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors text-right w-full text-inherit"
          >
            {item.title || 'קובץ ללא כותרת'}
          </button>
        </h4>
        {item.author && <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1">{item.author}</p>}
      </div>
    </article>
  );
}